/**
 * dev121 — Campana de notificaciones del topbar.
 *
 * Botón con badge de conteo (color por severidad — error rojo, warning
 * amarillo, info gris). Click abre dropdown con feed de items recientes.
 * Cada item es clickable → navega al link y cierra el dropdown.
 *
 * Polling cada 30s vía useNotifications.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, CheckCircle2, AlertCircle, Wallet,
  CalendarClock, Workflow, AlarmClock, CheckCheck, Eye, EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNotifications, type NotificationItem, type NotifTipo } from "./api";
import { useReadNotifications } from "./useReadNotifications";

const TIPO_ICON: Record<NotifTipo, typeof Bell> = {
  pago: Wallet,
  promesa_hoy: CalendarClock,
  promesa_vencida: AlarmClock,
  job_error: Workflow,
  suspension_hoy: AlertCircle,
};

const SEVERITY_DOT: Record<NotificationItem["severity"], string> = {
  info: "bg-blue-500",
  warning: "bg-amber-500",
  error: "bg-destructive",
};

const SEVERITY_BADGE: Record<NotificationItem["severity"], string> = {
  info: "bg-blue-500 text-white",
  warning: "bg-amber-500 text-white",
  error: "bg-destructive text-white",
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "ahora";
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false); // dev122: toggle ver todas / solo no-leídas
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  // dev122: estado de leídos persistido en localStorage
  const allItems = data?.items ?? [];
  const allIds = useMemo(() => allItems.map((i) => i.id), [allItems]);
  const { isRead, markRead, markAllRead, unreadCount } = useReadNotifications(allIds);

  // Items a mostrar: por default solo no-leídas; si toggle ON, todas
  const visibleItems = useMemo(
    () => (showAll ? allItems : allItems.filter((i) => !isRead(i.id))),
    [showAll, allItems, isRead],
  );

  // Cierra el dropdown al hacer click fuera
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Conteo de no-leídas por severidad para el badge
  const unreadBySev = useMemo(() => {
    const acc = { info: 0, warning: 0, error: 0 };
    for (const it of allItems) {
      if (!isRead(it.id)) acc[it.severity]++;
    }
    return acc;
  }, [allItems, isRead]);

  // Color del badge según mayor severidad NO-LEÍDA
  const badgeColor = unreadBySev.error > 0
    ? SEVERITY_BADGE.error
    : unreadBySev.warning > 0
    ? SEVERITY_BADGE.warning
    : SEVERITY_BADGE.info;

  function handleItemClick(item: NotificationItem) {
    markRead(item.id);
    setOpen(false);
    navigate(item.link);
  }

  function handleMarkAllRead() {
    markAllRead(allIds);
  }

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notificaciones"
        className="relative"
      >
        <Bell size={15} />
        {unreadCount > 0 && (
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold",
              badgeColor,
            )}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-[380px] max-h-[480px] overflow-hidden rounded-md border bg-popover shadow-lg flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="flex items-center gap-2">
              <Bell size={13} className="text-muted-foreground" />
              <span className="text-xs font-semibold">Notificaciones</span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold text-primary">
                  {unreadCount} sin leer
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {/* Toggle ver todas / solo no-leídas */}
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="rounded p-1 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                title={showAll ? "Solo mostrar no leídas" : "Mostrar todas (incluyendo leídas)"}
              >
                {showAll ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
              {/* Marcar todas como leídas */}
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="rounded p-1 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="Marcar todas como leídas"
                >
                  <CheckCheck size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                Cargando…
              </div>
            ) : visibleItems.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
                <CheckCircle2 size={20} className="text-emerald-500" />
                {allItems.length === 0 ? "Sin novedades" : "Todas leídas"}
                {allItems.length > 0 && !showAll && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="text-[10px] text-primary hover:underline"
                  >
                    Ver {allItems.length} leída(s)
                  </button>
                )}
              </div>
            ) : (
              <ul className="divide-y">
                {visibleItems.map((item) => {
                  const Icon = TIPO_ICON[item.tipo] ?? Bell;
                  const read = isRead(item.id);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleItemClick(item)}
                        className={cn(
                          "w-full px-3 py-2 hover:bg-accent text-left flex gap-2 group transition-opacity",
                          read && "opacity-50",
                        )}
                      >
                        <div className="relative shrink-0 mt-0.5">
                          <Icon size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                          {!read && (
                            <span
                              className={cn(
                                "absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full",
                                SEVERITY_DOT[item.severity],
                              )}
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={cn(
                              "text-xs truncate",
                              read ? "font-normal" : "font-medium",
                            )}>
                              {item.title}
                            </span>
                            <span className="shrink-0 text-[9px] text-muted-foreground">
                              {relativeTime(item.occurred_at)}
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {item.description}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-3 py-1.5 text-center">
            <span className="text-[9px] text-muted-foreground">
              Actualiza cada 30 segundos
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
